import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../Utility/Input/Input";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import SingleDatePicker from "../../../Utility/DatePickerRange/SingleDatePicker";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "../../../utils/dateUtils";
import FileDropzone from "../../../Utility/FileDropzone/FileDropzone";
import MiniEditor from "../MiniEditor";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { buildCreateTaskSchema } from "../../../FormsValidations/Validations";
import {
  buildWorkItemSubmitBody,
  buildBugDetailsFromFormFields,
} from "../../../utils/buildWorkItemSubmitBody";
import ErrorMsg from "../../../Utility/ErrorMsg";
import Typography from "../../../Utility/Typography";
import Checkbox from "../../../Utility/Checkbox/Checkbox";
import { isClientOnHold } from "../../../constants/clientOnHoldRules";
import { fetchProjectsData, createWorkItem } from "../../../Redux/Thunks/projectManagementThunk";
import { validateUploadFiles } from "../../../utils/fileUploadValidation";
import { getThunkRejectMessage } from "../../../utils/thunkErrorMessage";
import { get } from "../../../Services/ApiBase";
import { API_ENDPOINTS } from "../../../Services/ApiEndPoints";
import { normalizeWorkItemFromApi } from "../../Projects/projectManagementNormalizers";
import { fetchClientsData } from "../../../Redux/Thunks/clientsThunk";
import { fetchSystemList } from "../../../Redux/Thunks/systemListsThunk";
import { fetchUsersData } from "../../../Redux/Thunks/usersThunk";
import toast from "react-hot-toast";
import {
  parseWorkItemEstimateInputToMinutes,
  sanitizeWorkItemEstimateInput,
} from "../../../utils/workItemEstimateInput";
import {
  selectProjectsData,
  selectProjectsAllListData,
  selectProjectsLoading,
} from "../../../Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "../../../Redux/Slices/clientsSlice";
import { selectUsersData } from "../../../Redux/Slices/usersSlice";
import { selectAuthUserId } from "../../../Redux/Slices/authSlice";
import {
  getListFromApiResponse,
  preferListFromPropOrStore,
  getAllowedProjects,
  mapProjectToCreateTaskOption,
  mapUserToAssigneeOptionForDropdown,
  filterAssigneesForWorkItemType,
  isEpicWorkItemTypeLabel,
  mapSystemListItemToOption,
  TYPE_ICON_MAP,
  TYPE_FALLBACK_OPTIONS,
  PRIORITY_ICON_MAP,
  PRIORITY_FALLBACK_OPTIONS,
  resolveDefaultMediumPriorityId,
  resolvePriorityIdForWorkItemApi,
  ENVIRONMENT_FALLBACK_OPTIONS,
  REASON_OF_BUG_OPTIONS,
  LABEL_FALLBACK_OPTIONS,
} from "../issueMappers";

const getTodayLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function normalizeAttachmentFiles(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((item) => item?.file ?? item).filter(Boolean);
}

const CREATE_TASK_API_OPTIONS = Object.freeze({
  skipToast: true,
  suppressGlobalErrorModal: true,
});

const CreateTaskForm = ({
  projectsList: projectsListProp,
  clientsList: clientsListProp,
  currentProjectId,
  onSuccess,
  onSubmittingChange,
}) => {
  const dispatch = useDispatch();
  /** Paginated list vs GET /projects?all — full list lives in `allListData` only (see projectsManagementSlice). */
  const projectsPagedData = useSelector(selectProjectsData);
  const projectsAllListData = useSelector(selectProjectsAllListData);
  /** Merge paged + full list so new projects (often only on paged slice) still resolve labels in dropdowns. */
  const projectsFromStore = useMemo(() => {
    const allList = getListFromApiResponse(projectsAllListData);
    const pagedList = getListFromApiResponse(projectsPagedData);
    if (allList.length > 0 && pagedList.length > 0) {
      const byId = new Map();
      for (const p of allList) {
        const id = String(p?.id ?? p?.projectId ?? "");
        if (id) byId.set(id, p);
      }
      for (const p of pagedList) {
        const id = String(p?.id ?? p?.projectId ?? "");
        if (id && !byId.has(id)) byId.set(id, p);
      }
      return { list: Array.from(byId.values()) };
    }
    return projectsAllListData ?? projectsPagedData;
  }, [projectsAllListData, projectsPagedData]);
  const clientsData = useSelector(selectClientsData);
  const projectsLoading = useSelector(selectProjectsLoading);
  const usersData = useSelector(selectUsersData);
  const currentUserId = useSelector(selectAuthUserId);
  const [workItemTypeList, setWorkItemTypeList] = useState([]);
  const [priorityList, setPriorityList] = useState([]);
  const [severityList, setSeverityList] = useState([]);
  const [environmentOptions, setEnvironmentOptions] = useState(ENVIRONMENT_FALLBACK_OPTIONS);
  const [, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submitInFlightRef = useRef(false);
  const [parentWorkItemOptions, setParentWorkItemOptions] = useState([]);
  const [parentWorkItemsLoading, setParentWorkItemsLoading] = useState(false);
  const prevProjectIdForParentResetRef = useRef(null);

  const projectsList = preferListFromPropOrStore(projectsListProp, projectsFromStore);
  const clientsList = preferListFromPropOrStore(clientsListProp, clientsData);

  // Ensure project and client lists are loaded for Project (Parent) dropdown
  useEffect(() => {
    if (!projectsList?.length) dispatch(fetchProjectsData({ getAll: true }));
    if (!clientsList?.length) dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch, projectsList?.length, clientsList?.length]);

  // Always hit GET /users/all when the create form mounts so assignee options match latest users.
  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true, force: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("WORK_ITEM_TYPE"))
      .unwrap()
      .then((list) => setWorkItemTypeList(getListFromApiResponse(list)))
      .catch(() => setWorkItemTypeList([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("PRIORITY"))
      .unwrap()
      .then((list) => {
        const mapped = getListFromApiResponse(list);
        setPriorityList(mapped);
      })
      .catch(() => {
        setPriorityList([]);
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("SEVERITY"))
      .unwrap()
      .then((list) => setSeverityList(getListFromApiResponse(list)))
      .catch(() => setSeverityList([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("ENVIRONMENT"))
      .unwrap()
      .then((list) => {
        const mapped = getListFromApiResponse(list).map((item) =>
          mapSystemListItemToOption(item, {})
        );
        setEnvironmentOptions(mapped.length > 0 ? mapped : ENVIRONMENT_FALLBACK_OPTIONS);
      })
      .catch(() => {
        setEnvironmentOptions(ENVIRONMENT_FALLBACK_OPTIONS);
      });
  }, [dispatch]);

  const allowedProjects = getAllowedProjects(projectsList, clientsList, isClientOnHold);
  /** Creating from a project route/board — lock project to that context (global create passes no id). */
  const isProjectScoped = Boolean(currentProjectId && String(currentProjectId).trim());
  const baseProjectOptions = allowedProjects
    .map((p) => mapProjectToCreateTaskOption(p))
    .filter((o) => o.value !== "" && o.value != null);
  // Ensure the currently-selected project (from URL) is present in options so the dropdown
  // can display the project name even if it was filtered out by allowedProjects.
  const currentProjectOption = currentProjectId
    ? projectsList
        .map((p) => mapProjectToCreateTaskOption(p))
        .find((o) => String(o.value) === String(currentProjectId))
    : null;
  const projectOptions =
    currentProjectOption &&
    !baseProjectOptions.some((o) => String(o.value) === String(currentProjectOption.value))
      ? [currentProjectOption, ...baseProjectOptions]
      : baseProjectOptions;
  const projectsLoadingOrEmpty = projectsLoading || !projectsList?.length;

  const typeOptions =
    workItemTypeList.length === 0
      ? TYPE_FALLBACK_OPTIONS
      : workItemTypeList.map((item) => mapSystemListItemToOption(item, TYPE_ICON_MAP));

  const priorityOptions =
    priorityList.length === 0
      ? PRIORITY_FALLBACK_OPTIONS
      : priorityList.map((item) => mapSystemListItemToOption(item, PRIORITY_ICON_MAP));

  const severityOptions = severityList.map((item) => mapSystemListItemToOption(item, {}));

  const [description, setDescription] = useState("");

  const typeIdsRequiringParentEpic = useMemo(
    () => typeOptions.filter((o) => o.label === "Task" || o.label === "Bug").map((o) => o.value),
    [typeOptions]
  );

  const createTaskValidationSchema = useMemo(
    () => buildCreateTaskSchema({ typeIdsRequiringParent: typeIdsRequiringParentEpic }),
    [typeIdsRequiringParentEpic]
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createTaskValidationSchema),
    shouldUseNativeValidation: false,
    defaultValues: {
      type: "",
      project: "",
      parentId: "",
      priority: "",
      assignee: "",
      summary: "",
      attachments: [],
      originalEstimate: "",
      storyPoints: "",
      startDate: getTodayLocalDate(),
      dueDate: "",
      severityId: "",
      environment: [],
      reproductionSteps: "",
      isBacklog: false,
    },
  });

  // Resolve project ID for work-item fetch (selected project or current project from URL)
  const selectedProjectId =
    watch("project")?.trim() || (currentProjectId && String(currentProjectId).trim()) || null;

  useEffect(() => {
    const next = selectedProjectId?.trim() || null;
    const prev = prevProjectIdForParentResetRef.current;
    prevProjectIdForParentResetRef.current = next;
    if (prev != null && next !== prev) {
      setValue("parentId", "", { shouldValidate: false, shouldDirty: false });
      clearErrors("parentId");
    }
  }, [selectedProjectId, setValue, clearErrors]);

  // Parent (Epic) dropdown — GET /projects/:projectId/work-items/epics (Bearer on ApiBase)
  useEffect(() => {
    if (!selectedProjectId) {
      setParentWorkItemOptions([]);
      return;
    }
    setParentWorkItemsLoading(true);
    const endpoint = API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_EPICS(selectedProjectId);
    get(endpoint)
      .then((response) => {
        const raw = response?.data ?? response;
        const dataWrapper = raw?.data ?? raw;
        const rawList = Array.isArray(dataWrapper)
          ? dataWrapper
          : Array.isArray(dataWrapper?.data)
            ? dataWrapper.data
            : [];
        const list = rawList.map(normalizeWorkItemFromApi);
        const options = (list || []).map((item) => ({
          value: item.id,
          label: `${item.issueKey || item.itemKey || item.id} - ${item.title || "Untitled"}`,
        }));
        setParentWorkItemOptions(options);
      })
      .catch(() => setParentWorkItemOptions([]))
      .finally(() => setParentWorkItemsLoading(false));
  }, [selectedProjectId]);

  // When opened from a project's Work Items tab, pre-fill project from URL id
  useEffect(() => {
    if (currentProjectId && String(currentProjectId).trim()) {
      setValue("project", String(currentProjectId).trim(), { shouldValidate: false });
    }
  }, [currentProjectId, setValue]);

  const typeValue = watch("type");
  const selectedTypeLabel = typeOptions.find((o) => o.value === typeValue)?.label ?? "";

  const assigneeOptionsWithAvatar = useMemo(() => {
    const users = getListFromApiResponse(usersData);
    const list = filterAssigneesForWorkItemType(users, selectedTypeLabel);
    return list.map(mapUserToAssigneeOptionForDropdown).filter((o) => o.value);
  }, [usersData, selectedTypeLabel]);

  useEffect(() => {
    if (!isEpicWorkItemTypeLabel(selectedTypeLabel)) return;
    const current = getValues("assignee");
    if (!current) return;
    const allowed = assigneeOptionsWithAvatar.some((o) => o.value === String(current));
    if (!allowed) {
      setValue("assignee", "", { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedTypeLabel, assigneeOptionsWithAvatar, getValues, setValue]);

  useEffect(() => {
    const mediumId = resolveDefaultMediumPriorityId(priorityList);
    if (!mediumId) return;
    const current = getValues("priority");
    if (current === undefined || current === null || String(current).trim() === "") {
      setValue("priority", mediumId, { shouldValidate: true, shouldDirty: false });
    }
  }, [priorityList, getValues, setValue]);

  useEffect(() => {
    if (selectedTypeLabel !== "Task" && selectedTypeLabel !== "Bug") {
      setValue("parentId", "", { shouldValidate: true });
    }
  }, [selectedTypeLabel, setValue]);

  const todayDate = getTodayLocalDate();
  const startDateValue = watch("startDate");
  const dueDateMin = startDateValue && startDateValue >= todayDate ? startDateValue : todayDate;

  const originalEstimateRegister = register("originalEstimate");

  const onSubmit = async (data) => {
    if (submitInFlightRef.current) return;
    setSubmitError("");

    // Use project ID from URL when on a project's Work Items tab; otherwise from Project dropdown
    const projectId =
      (currentProjectId && String(currentProjectId).trim()) || data.project?.trim() || null;
    if (!projectId) {
      const msg = "Please select a project.";
      setSubmitError(msg);
      toast.error(msg);
      return;
    }
    if (projectsList.length > 0 && clientsList.length > 0) {
      const project = projectsList.find(
        (p) =>
          (p.id ?? p.projectId) === projectId || String(p.id ?? p.projectId) === String(projectId)
      );
      if (project) {
        const client = clientsList.find((c) => c.clientName === project.clientName);
        if (client && isClientOnHold(client)) {
          const msg = "Cannot add work item. This project's client is On Hold.";
          setSubmitError(msg);
          toast.error(msg);
          return;
        }
      }
    }

    const estimateTrimmed = String(data.originalEstimate ?? "").trim();
    if (estimateTrimmed) {
      const parsed = parseWorkItemEstimateInputToMinutes(estimateTrimmed);
      if (!parsed.ok) {
        const msg = parsed.error || "Invalid original estimate.";
        setSubmitError(msg);
        toast.error(msg);
        return;
      }
    }

    const attachmentFiles = normalizeAttachmentFiles(data.attachments);
    if (attachmentFiles.length > 0) {
      const attachmentValidationError = await validateUploadFiles(attachmentFiles, {
        inspectContent: false,
      });
      if (attachmentValidationError) {
        setSubmitError(attachmentValidationError);
        toast.error(attachmentValidationError);
        return;
      }
    }

    submitInFlightRef.current = true;
    setSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const selectedLabel = typeOptions.find((o) => o.value === data.type)?.label ?? "";
      const isBug = selectedLabel === "Bug";
      const assigneeId = data.assignee?.trim() || currentUserId || null;
      const body = buildWorkItemSubmitBody({
        typeId: data.type?.trim() || null,
        typeLabel: selectedLabel,
        title: data.summary?.trim() ?? "",
        description: description?.trim() || null,
        priorityId:
          resolvePriorityIdForWorkItemApi(data.priority, priorityList, priorityOptions) || null,
        assigneeId,
        parentId: data.parentId,
        originalEstimate: estimateTrimmed,
        storyPoints: Number(data.storyPoints) || 0,
        startDate: data.startDate,
        dueDate: data.dueDate,
        isBacklog: data.isBacklog,
        bugDetails: isBug ? buildBugDetailsFromFormFields(data) : undefined,
      });

      await dispatch(
        createWorkItem({
          projectId: String(projectId),
          body,
          ...(attachmentFiles.length > 0 ? { files: attachmentFiles } : {}),
          apiOptions: CREATE_TASK_API_OPTIONS,
        })
      ).unwrap();

      toast.success("Work item created successfully.");
      onSuccess?.();
    } catch (err) {
      const msg = getThunkRejectMessage(
        err,
        attachmentFiles.length > 0
          ? "Failed to create work item. Attachment upload did not succeed."
          : "Failed to create work item."
      );
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  return (
    <div className="space-y-5">
      <form
        id="create-task-form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5"
      >
        {submitError ? (
          <div
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2"
            role="alert"
          >
            <i className="ri-error-warning-line mt-0.5 shrink-0" aria-hidden />
            <span>{submitError}</span>
          </div>
        ) : null}
        <div className="w-2/4">
          <Controller
            name="project"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label={isProjectScoped ? "Project (current)" : "Project"}
                required
                disabled={isProjectScoped}
                clearable={!isProjectScoped}
                options={projectOptions}
                searchable={true}
                placeholder={
                  projectsLoadingOrEmpty && projectOptions.length === 0
                    ? "Loading projects..."
                    : "Select project"
                }
              />
            )}
          />
          <ErrorMsg message={errors.project?.message} />
        </div>

        <div className="w-2/4">
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label={
                  <>
                    Work type
                    <Typography variant="span1" className="text-red-600 pl-1" component="span">
                      *
                    </Typography>
                  </>
                }
                options={typeOptions}
              />
            )}
          />
          <ErrorMsg message={errors.type?.message} />
        </div>

        {(selectedTypeLabel === "Task" || selectedTypeLabel === "Bug") && (
          <div className="w-2/4">
            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  label={
                    <>
                      Parent (Epic)
                      <Typography variant="span1" className="text-red-600 pl-1" component="span">
                        *
                      </Typography>
                    </>
                  }
                  options={parentWorkItemOptions}
                  placeholder={parentWorkItemsLoading ? "Loading..." : "Select epic"}
                  disabled={parentWorkItemsLoading || !selectedProjectId}
                />
              )}
            />
            <ErrorMsg message={errors.parentId?.message} />
          </div>
        )}

        <div className="w-full">
          <Typography className="font-medium mb-1">
            Summary
            <Typography variant="span1" className="text-red-600 pl-1" component="span">
              *
            </Typography>
          </Typography>

          <Input
            {...register("summary")}
            className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
              errors.summary ? "border-badge-danger" : ""
            }`}
            placeholder="Enter task summary"
            noWrapper={true}
          />

          <ErrorMsg message={errors.summary?.message} />
        </div>

        {/* Bug-only fields: Reason of Bug, Severity, Environment, Reproduction Steps */}
        {selectedTypeLabel === "Bug" && (
          <>
            <div className="w-2/4">
              <Controller
                name="reasonOfBug"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    label="Reason of Bug"
                    options={REASON_OF_BUG_OPTIONS}
                    placeholder="Select..."
                  />
                )}
              />
            </div>
            <div className="w-2/4">
              <Controller
                name="severityId"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    label="Severity"
                    options={severityOptions}
                    placeholder="Select severity"
                  />
                )}
              />
            </div>
            <div className="w-2/4">
              <Controller
                name="environment"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    label="Environment"
                    options={environmentOptions}
                    placeholder="Select environment(s)"
                    multiselect
                    allowAddOption
                    onAddOption={(newOption) => {
                      setEnvironmentOptions((prev) => [...prev, newOption]);
                    }}
                  />
                )}
              />
            </div>
            <div className="w-full">
              <Input
                label="Reproduction Steps"
                type="textarea"
                rows={5}
                {...register("reproductionSteps")}
                className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary min-h-[100px] resize-y"
                placeholder="Describe steps to reproduce (multiple lines)"
              />
            </div>
          </>
        )}

        <div className="w-2/4">
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label={
                  <>
                    Priority
                    <Typography variant="span1" className="text-red-600 pl-1" component="span">
                      *
                    </Typography>
                  </>
                }
                options={priorityOptions}
                placeholder="Select priority"
              />
            )}
          />
          <ErrorMsg message={errors.priority?.message} />
        </div>
        <Controller
          name="assignee"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              label="Assignee"
              placeholder="Assign user..."
              options={assigneeOptionsWithAvatar}
              searchable={true}
            />
          )}
        />

        {/* label for epic and task */}
        {selectedTypeLabel !== "Bug" && (
          <div className="w-2/4">
            <Controller
              name="label"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  label="Labels"
                  options={LABEL_FALLBACK_OPTIONS}
                  placeholder="Select label"
                  // searchable
                  multiselect
                />
              )}
            />
          </div>
        )}
        {/* end label for epic and task */}

        <Typography variant="label" className="block mb-1 text-sm font-medium text-text-primary">
          Description
        </Typography>
        <MiniEditor
          placeholder="Type description..."
          value={description}
          onChange={setDescription}
          showToolbar={true}
          showActions={false}
          minHeight="120px"
          enableImageResize={true}
        />

        <FileDropzone
          label="Attachments"
          onFilesChange={(files) => setValue("attachments", files)}
          maxFiles={10}
          maxSizeMB={10}
          inspectContent={false}
        />

        <div className="w-2/4">
          <Input
            label="Original estimate"
            {...originalEstimateRegister}
            type="text"
            autoComplete="off"
            className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
            placeholder="e.g. 2w, 5h, 1d 2h, 120m"
            error={errors.originalEstimate?.message}
            onChange={(e) => {
              const next = sanitizeWorkItemEstimateInput(e.target.value);
              // Uncontrolled register() — browser keeps typed chars unless we sync the DOM.
              e.target.value = next;
              originalEstimateRegister.onChange({
                ...e,
                target: { ...e.target, value: next, name: e.target.name },
              });
            }}
          />
        </div>
        {/* <div className="w-2/4">
                    <Input
                        label="Story Points"
                        {...register("storyPoints")}
                        type="number"
                        min={0}
                        className="w-2/4 border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                        placeholder="e.g. 5"
                        noWrapper={true}
                    />
                </div> */}
        <div className="w-2/4">
          <Typography variant="label" className="block mb-1 text-sm font-medium text-text-primary">
            Start Date
            <Typography variant="span1" className="text-red-600 pl-1" component="span">
              *
            </Typography>
          </Typography>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <SingleDatePicker
                value={field.value ? { date: field.value } : null}
                onChange={(val) => field.onChange(val?.date ?? "")}
                minDate={todayDate}
                placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                className={errors.startDate ? "!border-red-500" : ""}
              />
            )}
          />
          <ErrorMsg message={errors.startDate?.message} />
        </div>
        <div className="w-2/4">
          <Typography variant="label" className="block mb-1 text-sm font-medium text-text-primary">
            Due Date
            <Typography
              variant="span1"
              className="text-text-secondary pl-1 font-normal"
              component="span"
            >
              (optional)
            </Typography>
          </Typography>
          <Controller
            name="dueDate"
            control={control}
            render={({ field }) => (
              <SingleDatePicker
                value={field.value ? { date: field.value } : null}
                onChange={(val) => field.onChange(val?.date ?? "")}
                minDate={dueDateMin}
                placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                className={errors.dueDate ? "!border-red-500" : ""}
              />
            )}
          />
          <ErrorMsg message={errors.dueDate?.message} />
        </div>

        <Controller
          name="isBacklog"
          control={control}
          render={({ field }) => (
            <Checkbox
              name={field.name}
              id="create-task-is-backlog"
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              label="Add work item as backlog"
            />
          )}
        />
      </form>
    </div>
  );
};

export default CreateTaskForm;
